/**
 * Editor de plantas — desenhar a unidade dentro do produto.
 *
 * Existe porque exigir um arquivo pronto exclui quem não tem: boa parte das
 * operações não tem planta em PDF, tem uma folha na parede ou nada. E porque
 * imagem importada não é editável — mudou o layout, refaz tudo em outro
 * software, e na prática ninguém refaz: a planta envelhece mentindo.
 *
 * O QUE ELE É, e o que não é. Três primitivas — cômodo, parede, porta — mais
 * rótulo solto. Não é CAD e não deve virar: o objetivo é uma planta
 * RECONHECÍVEL pra pendurar marcador em cima, não um projeto executivo. Cada
 * ferramenta a mais é uma decisão a mais na frente de quem só quer terminar.
 *
 * DECISÕES QUE SUSTENTAM ISSO:
 *
 *  • NADA DE BIBLIOTECA DE CANVAS. O desenho é um `<svg>` com retângulos e
 *    linhas, e o ponteiro é aritmética de coordenada. Qualquer biblioteca de
 *    canvas traria seu próprio modelo de objeto, sua serialização e seus
 *    eventos — e brigaria exatamente no marcador de ativo, que é a razão da
 *    tela existir.
 *
 *  • O DOCUMENTO INTEIRO VAI E VOLTA. Nada de gravar forma por forma: o
 *    editor tem o documento em mãos e manda o resultado. Mesclagem parcial
 *    daria a ilusão de edição colaborativa que isto não tem.
 *
 *  • DESFAZER É OBRIGATÓRIO, não conforto. A primeira coisa que alguém faz
 *    num editor é errar, e sem desfazer o erro vira medo de usar.
 *
 *  • SAIR COM ALTERAÇÃO PENDENTE AVISA. Desenho perdido por fechar a aba é a
 *    forma mais rápida de garantir que ninguém desenhe uma segunda vez.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DoorOpen, Grid3x3, MousePointer2, Redo2, Save, Square, Trash2, Type, Undo2,
} from "lucide-react";
import toast from "react-hot-toast";
import clsx from "clsx";

import { sitesApi } from "@/api";
import { apiErrorMessage } from "@/api/client";
import { avisarMudanca } from "@/lib/sincronia";
import { FormasDaPlanta } from "@/components/FormasDaPlanta";
import { Button } from "@/components/Button";
import {
  GRADE, ROTULO_DA_COR, TEMAS, alcasDaForma, atingiu, caixaDaForma,
  desenhoVazio, encaixar, moverForma, novoId, redimensionar,
  tema as temaDaPlanta, type CorDeComodo, type Desenho, type Forma,
  type TemaDaPlanta,
} from "@/lib/plantaDesenho";

type Ferramenta = "selecionar" | "comodo" | "parede" | "porta" | "rotulo";

const FERRAMENTAS: { id: Ferramenta; rotulo: string; Icone: typeof Square }[] = [
  { id: "selecionar", rotulo: "Selecionar", Icone: MousePointer2 },
  { id: "comodo",     rotulo: "Cômodo",     Icone: Square },
  { id: "parede",     rotulo: "Parede",     Icone: Grid3x3 },
  { id: "porta",      rotulo: "Porta",      Icone: DoorOpen },
  { id: "rotulo",     rotulo: "Texto",      Icone: Type },
];

/** Arrasto menor que isto é clique, não desenho. */
const MINIMO_PRA_DESENHAR = 8;


interface Props {
  siteId: string;
  inicial: Desenho | null;
  /** Imagem importada, se houver — vira o fundo pra desenhar por cima. */
  fundo?: string | null;
  aoSair: () => void;
}

export function EditorDePlanta({ siteId, inicial, fundo, aoSair }: Props) {
  const qc = useQueryClient();
  const svgRef = useRef<SVGSVGElement>(null);

  // Histórico como pilha de documentos inteiros. Um documento é kilobytes e
  // o limite é de 400 formas: guardar o estado completo custa menos memória
  // do que custaria a complexidade de guardar operações reversíveis.
  const [historico, setHistorico] = useState<Desenho[]>([inicial ?? desenhoVazio()]);
  const [passo, setPasso] = useState(0);
  const doc = historico[passo];
  const t = temaDaPlanta(doc.tema);

  const [salvo, setSalvo] = useState<string>(() => JSON.stringify(inicial ?? desenhoVazio()));
  const sujo = JSON.stringify(doc) !== salvo;

  const [ferramenta, setFerramenta] = useState<Ferramenta>("selecionar");
  const [grade, setGrade] = useState(true);
  /** Mostrar o calco. Só na sessão — não é preferência de planta, é o gesto
   *  de "deixa eu ver como ficou o traço sozinho" no meio do desenho. */
  const [mostrarFundo, setMostrarFundo] = useState(true);
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const [rascunho, setRascunho] = useState<Forma | null>(null);
  // `alca` presente = está redimensionando aquela forma, não criando nem
  // movendo. Os três gestos passam pelo mesmo par de handlers do <svg>
  // porque o ponteiro é um só.
  const arrasto = useRef<
    { x: number; y: number; forma: Forma | null; alca?: string } | null>(null);

  const aplicar = useCallback((novo: Desenho) => {
    setHistorico((h) => [...h.slice(0, passo + 1), novo]);
    setPasso((p) => p + 1);
  }, [passo]);

  const desfazer = useCallback(() => setPasso((p) => Math.max(0, p - 1)), []);
  const refazer = useCallback(
    () => setPasso((p) => Math.min(historico.length - 1, p + 1)), [historico.length]);

  const salvarMut = useMutation({
    mutationFn: (d: Desenho) => sitesApi.gravarDesenho(siteId, d),
    onSuccess: (_r, d) => {
      setSalvo(JSON.stringify(d));
      toast.success("Planta salva");
      // `refetch` e não `invalidate`: invalidar só marca como velho, e a
      // consulta pode ficar marcada sem nunca recarregar.
      void qc.refetchQueries({ queryKey: ["sites"] });
      void qc.refetchQueries({ queryKey: ["site", siteId, "desenho"] });
      // E avisa as OUTRAS abas — o dashboard que abriu este editor tem cache
      // próprio e não tem como saber que a planta mudou.
      avisarMudanca([["sites"], ["site", siteId, "desenho"]]);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  // Fechar a aba com desenho não salvo é a forma mais rápida de garantir que
  // ninguém desenhe uma segunda vez.
  useEffect(() => {
    if (!sujo) return;
    const avisar = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", avisar);
    return () => window.removeEventListener("beforeunload", avisar);
  }, [sujo]);

  useEffect(() => {
    function tecla(e: KeyboardEvent) {
      const alvo = e.target as HTMLElement | null;
      if (alvo && /^(INPUT|TEXTAREA|SELECT)$/.test(alvo.tagName)) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) refazer(); else desfazer();
      }
      if ((e.key === "Delete" || e.key === "Backspace") && selecionada) {
        e.preventDefault();
        aplicar({ ...doc, formas: doc.formas.filter((f) => f.id !== selecionada) });
        setSelecionada(null);
      }
      if (e.key === "Escape") { setSelecionada(null); setFerramenta("selecionar"); }
    }
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [desfazer, refazer, selecionada, doc, aplicar]);

  /**
   * Quantas unidades do documento equivalem a um alvo confortável de clique.
   *
   * Em pixels o alvo tem que ser sempre o mesmo (~10px); em unidades do
   * documento, não — um documento grande desenhado pequeno precisa de
   * tolerância MAIOR, não menor. Daí converter pela escala em vez de fixar
   * um número.
   */
  function toleranciaDeClique(): number {
    const caixa = svgRef.current?.getBoundingClientRect();
    if (!caixa?.width) return 10;
    return (doc.largura / caixa.width) * 10;
  }

  /** Ponto do ponteiro em coordenadas do DOCUMENTO, não da tela. */
  function ponto(ev: React.PointerEvent): { x: number; y: number } | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const caixa = svg.getBoundingClientRect();
    if (!caixa.width || !caixa.height) return null;
    // Regra de três direta: o `viewBox` é o documento e o SVG preenche a
    // caixa. É o mesmo motivo de o marcador usar fração — a tela muda de
    // tamanho, o documento não.
    return {
      x: ((ev.clientX - caixa.left) / caixa.width) * doc.largura,
      y: ((ev.clientY - caixa.top) / caixa.height) * doc.altura,
    };
  }

  function aoDescer(ev: React.PointerEvent) {
    const p = ponto(ev);
    if (!p) return;
    (ev.currentTarget as Element).setPointerCapture(ev.pointerId);

    if (ferramenta === "selecionar") {
      // De trás pra frente: o que foi desenhado por último está por cima, e
      // é nele que a pessoa pensa estar clicando.
      const achada = [...doc.formas].reverse()
        .find((f) => atingiu(f, p.x, p.y, toleranciaDeClique()));
      setSelecionada(achada?.id ?? null);
      arrasto.current = achada ? { x: p.x, y: p.y, forma: achada } : null;
      return;
    }

    if (ferramenta === "rotulo") {
      const texto = window.prompt("Texto");
      if (texto?.trim()) {
        aplicar({
          ...doc,
          formas: [...doc.formas, {
            id: novoId(), tipo: "rotulo",
            x: encaixar(p.x, grade), y: encaixar(p.y, grade),
            texto: texto.trim().slice(0, 60),
          }],
        });
      }
      setFerramenta("selecionar");
      return;
    }

    if (ferramenta === "porta") {
      aplicar({
        ...doc,
        formas: [...doc.formas, {
          id: novoId(), tipo: "porta",
          x: encaixar(p.x, grade), y: encaixar(p.y, grade),
          largura: 60, angulo: 0,
        }],
      });
      setFerramenta("selecionar");
      return;
    }

    arrasto.current = { x: encaixar(p.x, grade), y: encaixar(p.y, grade), forma: null };
  }

  function aoMover(ev: React.PointerEvent) {
    const a = arrasto.current;
    if (!a) return;
    const p = ponto(ev);
    if (!p) return;

    if (a.alca && a.forma) {
      setRascunho(redimensionar(a.forma, a.alca, p.x, p.y, grade));
      return;
    }

    if (ferramenta === "selecionar" && a.forma) {
      const dx = encaixar(p.x - a.x, grade);
      const dy = encaixar(p.y - a.y, grade);
      if (!dx && !dy) return;
      const movida = moverForma(a.forma, dx, dy);
      setRascunho(movida);
      return;
    }

    const x = encaixar(p.x, grade), y = encaixar(p.y, grade);
    if (ferramenta === "comodo") {
      setRascunho({
        id: "rascunho", tipo: "comodo",
        x: Math.min(a.x, x), y: Math.min(a.y, y),
        w: Math.max(1, Math.abs(x - a.x)), h: Math.max(1, Math.abs(y - a.y)),
        rotulo: "", cor: "neutro",
      });
    } else if (ferramenta === "parede") {
      setRascunho({ id: "rascunho", tipo: "parede", x1: a.x, y1: a.y, x2: x, y2: y });
    }
  }

  function aoSubir() {
    const a = arrasto.current;
    const r = rascunho;
    arrasto.current = null;
    setRascunho(null);
    if (!a || !r) return;

    if ((a.alca || ferramenta === "selecionar") && a.forma) {
      aplicar({
        ...doc,
        formas: doc.formas.map((f) => (f.id === a.forma!.id ? { ...r, id: f.id } as Forma : f)),
      });
      return;
    }

    // Arrasto curto é clique errado, não forma de 2px. Sem este piso, cada
    // clique fora do lugar deixa um resíduo invisível no documento.
    const c = caixaDaForma(r);
    if (Math.max(c.w, c.h) < MINIMO_PRA_DESENHAR) return;

    aplicar({ ...doc, formas: [...doc.formas, { ...r, id: novoId() }] });
    setFerramenta("selecionar");
  }

  const formaSelecionada = useMemo(
    () => doc.formas.find((f) => f.id === selecionada) ?? null,
    [doc.formas, selecionada]);

  // Durante o arrasto as alças precisam seguir o RASCUNHO, senão elas ficam
  // paradas no tamanho antigo enquanto a forma cresce debaixo delas.
  const formaVisivelSelecionada =
    rascunho && arrasto.current?.forma?.id === selecionada ? rascunho : formaSelecionada;

  function alterarSelecionada(patch: Partial<Comodoish>) {
    if (!formaSelecionada) return;
    aplicar({
      ...doc,
      formas: doc.formas.map((f) =>
        f.id === formaSelecionada.id ? ({ ...f, ...patch } as Forma) : f),
    });
  }

  const substituindo = !!arrasto.current?.forma;
  const visiveis = rascunho
    ? (substituindo
        ? doc.formas.map((f) => (f.id === arrasto.current!.forma!.id ? rascunho : f))
        : [...doc.formas, rascunho])
    : doc.formas;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-surface-300 p-0.5 dark:border-slate-700">
          {FERRAMENTAS.map(({ id, rotulo, Icone }) => (
            <button
              key={id}
              type="button"
              onClick={() => { setFerramenta(id); setSelecionada(null); }}
              aria-pressed={ferramenta === id}
              title={rotulo}
              className={clsx(
                "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium",
                ferramenta === id
                  ? "bg-brand-500 text-white"
                  : "text-slate-600 hover:bg-surface-100 dark:text-slate-300 dark:hover:bg-slate-800",
              )}
            >
              <Icone className="h-4 w-4" aria-hidden />
              <span className="hidden sm:inline">{rotulo}</span>
            </button>
          ))}
        </div>

        {fundo && (
          <button
            type="button"
            onClick={() => setMostrarFundo((v) => !v)}
            aria-pressed={mostrarFundo}
            className={clsx(
              "rounded-md border px-2.5 py-1.5 text-sm font-medium",
              mostrarFundo
                ? "border-brand-500 text-brand-700 dark:text-brand-300"
                : "border-surface-300 text-slate-500 dark:border-slate-700 dark:text-slate-400",
            )}
          >
            Calco
          </button>
        )}

        <button
          type="button"
          onClick={() => setGrade((g) => !g)}
          aria-pressed={grade}
          className={clsx(
            "rounded-md border px-2.5 py-1.5 text-sm font-medium",
            grade
              ? "border-brand-500 text-brand-700 dark:text-brand-300"
              : "border-surface-300 text-slate-500 dark:border-slate-700 dark:text-slate-400",
          )}
        >
          Grade
        </button>

        {/* O tema é do DOCUMENTO, não do app: a mesma planta quer fundo
            escuro numa TV no salão e fundo branco impressa. Guardar a
            escolha aqui é o que faz tela, painel e PDF concordarem. */}
        <select
          value={doc.tema ?? "claro"}
          onChange={(ev) => aplicar({ ...doc, tema: ev.target.value as TemaDaPlanta })}
          aria-label="Fundo da planta"
          className="input h-[34px] w-36 text-sm"
        >
          {Object.entries(TEMAS).map(([k, v]) => (
            <option key={k} value={k}>Fundo {v.rotulo.toLowerCase()}</option>
          ))}
        </select>

        <div className="flex gap-1">
          <Button variant="secondary" onClick={desfazer} disabled={passo === 0}
                  title="Desfazer (Ctrl+Z)">
            <Undo2 className="h-4 w-4" aria-hidden />
          </Button>
          <Button variant="secondary" onClick={refazer}
                  disabled={passo >= historico.length - 1} title="Refazer (Ctrl+Shift+Z)">
            <Redo2 className="h-4 w-4" aria-hidden />
          </Button>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {sujo && (
            <span className="text-xs text-amber-700 dark:text-amber-300">
              alterações não salvas
            </span>
          )}
          <Button variant="secondary" onClick={aoSair}>Fechar</Button>
          <Button onClick={() => salvarMut.mutate(doc)} loading={salvarMut.isPending}
                  disabled={!sujo}>
            <Save className="mr-1.5 h-4 w-4" aria-hidden />
            Salvar
          </Button>
        </div>
      </div>

      {formaSelecionada && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-surface-200 bg-surface-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-900/60">
          {formaSelecionada.tipo === "comodo" && (
            <>
              <input
                value={formaSelecionada.rotulo}
                onChange={(e) => alterarSelecionada({ rotulo: e.target.value.slice(0, 60) })}
                placeholder="Nome do cômodo"
                className="input h-8 w-48 text-sm"
              />
              <select
                value={formaSelecionada.cor}
                onChange={(e) => alterarSelecionada({ cor: e.target.value as CorDeComodo })}
                className="input h-8 w-40 text-sm"
              >
                {Object.entries(ROTULO_DA_COR).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </>
          )}
          {formaSelecionada.tipo === "porta" && (
            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              Abertura
              <input
                type="range" min={-180} max={180} step={15}
                value={formaSelecionada.angulo}
                onChange={(e) => alterarSelecionada({ angulo: Number(e.target.value) })}
              />
            </label>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              aplicar({ ...doc, formas: doc.formas.filter((f) => f.id !== formaSelecionada.id) });
              setSelecionada(null);
            }}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      )}

      <svg
        ref={svgRef}
        viewBox={`0 0 ${doc.largura} ${doc.altura}`}
        style={{ background: t.papel }}
        className={clsx(
          "w-full touch-none rounded-lg border border-surface-200 dark:border-slate-700",
          ferramenta === "selecionar" ? "cursor-default" : "cursor-crosshair",
        )}
        onPointerDown={aoDescer}
        onPointerMove={aoMover}
        onPointerUp={aoSubir}
      >
        {/* O papel segue o TEMA DO DESENHO, não o tema do app: são coisas
            diferentes, e quem decide é quem desenhou — a planta pode viver
            escura numa TV e clara no relatório do mesmo cliente. */}
        {grade && (
          <>
            <defs>
              <pattern id="grade" width={GRADE} height={GRADE} patternUnits="userSpaceOnUse">
                <path d={`M ${GRADE} 0 L 0 0 0 ${GRADE}`} fill="none"
                      stroke={t.grade} strokeWidth={1} />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grade)" />
          </>
        )}

        {fundo && mostrarFundo && (
          // A imagem importada vira calco: desenhar por cima de uma foto ou
          // scan é o caminho mais rápido pra uma planta fiel. Meio
          // transparente pra que o traço se destaque dela.
          <image href={fundo} x={0} y={0} width={doc.largura} height={doc.altura}
                 preserveAspectRatio="xMidYMid meet" opacity={0.45} />
        )}

        <FormasDaPlanta formas={visiveis} selecionada={selecionada} tema={doc.tema} />

        {/* Alças da forma selecionada.
            Sem elas, mudar o tamanho de um cômodo exigia apagar e desenhar
            de novo — perdendo o nome e a cor que alguém já escolheu. Na
            prática o desenho para de ser ajustado, que é o começo da planta
            que envelhece mentindo. */}
        {formaVisivelSelecionada && alcasDaForma(formaVisivelSelecionada).map((h) => {
          // A alça acompanha a escala da tela: em unidades do documento, um
          // tamanho fixo fica minúsculo num desenho grande e enorme num
          // pequeno. `toleranciaDeClique` já converte 10px pra unidades.
          const meia = Math.max(6, toleranciaDeClique());
          return (
            <rect
              key={h.id}
              x={h.x - meia} y={h.y - meia}
              width={meia * 2} height={meia * 2}
              rx={2}
              fill="#ffffff" stroke="#0ea5e9" strokeWidth={2}
              style={{ cursor: "nwse-resize", touchAction: "none" }}
              /*
               * OS TRÊS HANDLERS FICAM NA PRÓPRIA ALÇA, e não só o de
               * descer. Com a captura de ponteiro na alça, os eventos são
               * redirecionados pra ela; depender de eles borbulharem até o
               * <svg> pra continuar o gesto é uma aposta a mais sem
               * necessidade — e uma aposta que, quando falha, falha como
               * "a alça não faz nada".
               */
              onPointerDown={(ev) => {
                ev.stopPropagation();
                const p0 = ponto(ev);
                if (!p0 || !formaVisivelSelecionada) return;
                try {
                  (ev.currentTarget as Element).setPointerCapture(ev.pointerId);
                } catch { /* segue sem captura */ }
                arrasto.current = {
                  x: p0.x, y: p0.y,
                  forma: formaVisivelSelecionada, alca: h.id,
                };
              }}
              onPointerMove={(ev) => {
                const a = arrasto.current;
                if (!a?.alca || !a.forma) return;
                ev.stopPropagation();
                const p = ponto(ev);
                if (p) setRascunho(redimensionar(a.forma, a.alca, p.x, p.y, grade));
              }}
              onPointerUp={(ev) => { ev.stopPropagation(); aoSubir(); }}
              onPointerCancel={() => { aoSubir(); }}
            />
          );
        })}

      </svg>

      <p className="text-xs text-slate-500 dark:text-slate-400">
        Arraste para criar cômodos e paredes. Clique para posicionar portas e
        texto. Selecione uma forma para movê-la, renomeá-la ou puxar as alças
        azuis e mudar o tamanho — Delete apaga, Ctrl+Z desfaz.
      </p>
    </div>
  );
}

/** Campos editáveis pelo painel de propriedades. */
type Comodoish = { rotulo: string; cor: CorDeComodo; angulo: number };
